import isEqual from 'fast-deep-equal/es6/react';
import type { DependencyList } from 'react';
import { useRef } from 'react';

/**
 * useCallback() variant that uses deep equality comparison
 * instead of referential equality for dependency checking.
 *
 * Important! Profile before usage, in most scenarios referential check and re-computation
 * might be faster than deep equal comparison.
 *
 * Order:
 * - simple computation OR stable references -> use built-in React
 * - complex computation AND relatively small objects as dependencies -> useDeepMemo
 * - complex computation AND very complex objects as dependencies -> useHashMemo (create it if needed)
 */
export function useDeepMemo<T>(factory: () => T, deps: DependencyList): T {
  const ref = useRef<{ deps: DependencyList; value: T }>(null);

  if (!ref.current || !isEqual(ref.current.deps, deps)) {
    ref.current = { deps, value: factory() };
  }

  return ref.current.value;
}
